import { useMutation, useQuery } from '@tanstack/react-query'

import type { NlpSearchFlightsResponse } from '../types/chat.js'
import type { ExtractedBookingData } from '../types/extracted-booking.js'
import type {
  PriceOfferRequest,
  PriceOfferResponse,
  SearchFlightsRequest,
  SearchFlightsResponse,
} from '../types/research-bar.js'

import { apiClient } from '../lib/client.js'

const BOOKING_SESSION_STALE_TIME = 20 * 60 * 1000 // 20 minutes
const SEARCH_FLIGHTS_TIMEOUT_MS = 2 * 60 * 1000

const searchFlights = async (request: SearchFlightsRequest, signal: AbortSignal) => {
  const response = await apiClient.client.post<SearchFlightsResponse>(
    `/research_bar/search`,
    request,
    {
      signal,
      timeout: SEARCH_FLIGHTS_TIMEOUT_MS,
    }
  )
  return response.data
}

const searchFromQuery = async (query: string) => {
  const response = await apiClient.client.post<NlpSearchFlightsResponse>(
    '/chat/flight/search-from-query',
    { query },
    { timeout: 2 * 60 * 1000 }
  )
  return response.data
}

export const useSearchFlightsQuery = (request: null | SearchFlightsRequest) =>
  useQuery({
    enabled: request !== null,
    queryFn: ({ signal }) => (request ? searchFlights(request, signal) : null),
    queryKey: ['searchFlights', request],
    retry: false,
    staleTime: BOOKING_SESSION_STALE_TIME,
  })

export const useSearchFromQueryMutation = () =>
  useMutation({
    mutationFn: searchFromQuery,
  })

export const useFetchAffidiumBookingMutation = () =>
  useMutation({
    mutationFn: async (params: { accountCode?: string; bookingReference: string }) => {
      const response = await apiClient.client.get<ExtractedBookingData>(
        `/research_bar/bookings/${params.bookingReference}`,
        {
          params: params.accountCode ? { accountCode: params.accountCode } : undefined,
        }
      )
      return response.data
    },
  })

export const usePriceOfferQuery = (
  request: null | PriceOfferRequest,
  options?: { enabled?: boolean }
) =>
  useQuery({
    enabled: options?.enabled ?? !!request,
    queryFn: async () => {
      if (!request) throw new Error('No request provided')
      const response = await apiClient.client.get<PriceOfferResponse>('/research_bar/price', {
        params: request,
      })
      return response.data
    },
    queryKey: ['price-offer', request?.transactionKey, request?.offerKey, request?.journeyKeyArray],
    staleTime: BOOKING_SESSION_STALE_TIME,
  })
